import { useMemo } from "react";
import { Button, Container } from "@material-ui/core";
import { Alert } from "@mui/material";
import { useSelector } from "@xstate/store/react";
import { ShoppingCartOutlined } from "@material-ui/icons";

import Layout from "../../Shared/Layout";
import { useShoppingCartStore } from "../../stores/ShoppingCartStore";
import { Trans } from "../../utils/i18n";
import { useTransportProducts } from "../../hooks/useTransportProducts";
import FormPage from "../../Shared/FormPage";
import {FormState} from "../../FormComponents/FormState";
import { SuccessPage } from "../OrderEntry";
import { getClientCheckoutConfig } from "../../utils/getClientCheckoutConfig";

export default function PartsShopCheckout({ orderControllerConfig, requesttypes, client }) {
    const store = useShoppingCartStore();
    const products = useSelector(store, (state) => state.context.products);
    const transportProduct = useSelector(store, (state) => state.context.transportProduct);
    const {
        data: transportProductsData,
        isLoading: isTransportProductsLoading,
    } = useTransportProducts({
        products: products.map((product) => ({
            id: product.id,
            amount: Number(product.amount ?? 1),
        })),
    });

    const checkoutConfig = getClientCheckoutConfig({ client });

    const noTransportProductsAvailable =
        !isTransportProductsLoading &&
        transportProductsData?.transport?.length === 0;

    const defaultState = useMemo(() => {
        return {
            orderControllerConfig: orderControllerConfig ? { enableStepper: !!checkoutConfig.enableStepper, ...orderControllerConfig, } : { enableStepper: !!checkoutConfig.enableStepper, },
            ...(checkoutConfig?.defaultValues ? checkoutConfig.defaultValues : {}),
            ...(checkoutConfig?.initialState ? checkoutConfig.initialState : {}),
        };
    }, []);

    if (!products || products.length === 0) {
        return (
            <Container maxWidth="md" className="space-y-8">
                <div className="b2x-shadow-md bg-white p-8">
                    <div className="flex flex-col gap-4 items-center justify-center h-full w-full">
                        <ShoppingCartOutlined className="!size-20 text-secondary" />
                        <div className="flex flex-col items-center justify-center gap-2">
                            <div className="text-xl font-bold">
                                <Trans i18nKey="shoppingCart.empty.title">No products in the shopping cart</Trans>
                            </div>
                            <div className="text-gray-600">
                                <Trans i18nKey="shoppingCart.empty.description">Visit the shop to purchase products</Trans>
                            </div>
                        </div>
                        <Button
                            href={route("partsshop.index")}
                            variant="contained"
                            color="secondary"
                            className="font-bold"
                        >
                            <Trans i18nKey="shoppingCart.goToShop">Visit the shop</Trans>
                        </Button>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <FormState
            defaultState={{
                ...defaultState,
            }}
        >
            <Container maxWidth="md" className="space-y-8">
                {noTransportProductsAvailable && (
                    <Alert severity="error">
                        <Trans i18nKey="orderEntry.checkout.noTransportAvailable">
                            You cannot order part shipments greather 10KG.
                            Please place a second order
                        </Trans>
                    </Alert>
                )}

                {/*
                    The transport product is fetched asynchronously, so before fetching it will be null.
                    So the defaultValues are already being initialized with an empty transport_product_id.
                    When the product then is fetched, the defaultValues are NOT getting updated.
                    Therefore, the validation for transport product will always fill.
                    For now, we start rendering the form when the transport product is available.
                    In the future, we should enable syncing the defaultValues.
                */}
                {products.length > 0 && !isTransportProductsLoading && (
                    <div>
                        <FormPage
                            requesttypes={requesttypes}
                            orderEntryConfig={checkoutConfig}
                            defaultValues={{
                                products: products,
                                transport_product_id: transportProduct?.id,
                            }}
                            endpointUrl={route('order')}
                            SuccessPage={checkoutConfig?.success || SuccessPage}
                        />
                    </div>
                )}
            </Container>
        </FormState>
    );
}

PartsShopCheckout.layout = (page) => <Layout children={page} />;
